/**
 * @fileoverview Importar os feriados nacionais do ano (utils/nationalHolidays)
 * como datas fechadas. Vêm marcados os oficiais que ainda não estão cadastrados;
 * os facultativos (Carnaval, Páscoa, Corpus Christi) a pessoa marca se fechar.
 * Datas que já passaram ficam de fora.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, CircularProgress, Dialog, IconButton, MenuItem } from "@material-ui/core";
import { toast } from "react-toastify";
import { X } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { nationalHolidays } from "../../utils/nationalHolidays";
import ModernSelect from "../ModernSelect";
import { formatLocalDate } from "../Triage/helpers";
import useExceptionStyles from "./styles";

const t = (key, options) => i18n.t(`settings.schedules.exceptions.national.${key}`, options);

const pad = (n) => String(n).padStart(2, "0");
const localToday = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const NationalHolidaysDialog = ({ open, onClose, exceptions, onImported }) => {
  const classes = useExceptionStyles();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [selected, setSelected] = useState(() => new Set());
  const [importing, setImporting] = useState(false);

  const registered = useMemo(() => new Set(exceptions.map((exception) => exception.date)), [exceptions]);
  const holidays = useMemo(() => {
    const today = localToday();
    return nationalHolidays(year).filter((holiday) => holiday.date >= today);
  }, [year]);

  // Ao abrir (ou trocar o ano): marca os oficiais que ainda não estão cadastrados.
  useEffect(() => {
    if (!open) return;
    setSelected(new Set(holidays.filter((h) => !h.optional && !registered.has(h.date)).map((h) => h.date)));
  }, [open, holidays, registered]);

  const toggle = (date) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });

  const close = () => {
    if (!importing) onClose();
  };

  const handleImport = async () => {
    setImporting(true);
    let added = 0;
    try {
      for (const holiday of holidays.filter((h) => selected.has(h.date))) {
        try {
          await api.post("/schedule-exceptions", {
            date: holiday.date,
            isClosed: true,
            startTime: null,
            endTime: null,
            note: t(`names.${holiday.key}`),
          });
          added += 1;
        } catch (err) {
          // Data cadastrada enquanto o diálogo estava aberto (409): segue para a próxima.
          if (err?.response?.status !== 409) throw err;
        }
      }
      toast.success(t("imported", { count: added }));
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setImporting(false);
      if (added) onImported();
    }
  };

  return (
    <Dialog open={open} onClose={close} PaperProps={{ className: classes.paper }}>
      <div className={classes.dialogHeader}>
        <div>
          <h2 className={classes.dialogTitle}>{t("title")}</h2>
          <p className={classes.dialogDescription}>{t("description")}</p>
        </div>
        <IconButton size="small" className={classes.closeButton} onClick={close} disabled={importing} aria-label={t("cancel")}>
          <X size={18} strokeWidth={1.8} />
        </IconButton>
      </div>

      <div className={classes.dialogBody}>
        <div>
          <span className={classes.label}>{t("year")}</span>
          <ModernSelect
            size="small"
            value={year}
            onChange={(event) => setYear(event.target.value)}
            disabled={importing}
            MenuProps={{ disableScrollLock: true }}
            SelectDisplayProps={{ "aria-label": t("year") }}
          >
            {[thisYear, thisYear + 1].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </ModernSelect>
        </div>

        {holidays.length === 0 ? (
          <p className={classes.empty}>{t("empty")}</p>
        ) : (
          <div className={classes.holidayList}>
            {holidays.map((holiday) => {
              const isRegistered = registered.has(holiday.date);
              return (
                <label
                  key={holiday.date}
                  className={`${classes.holidayRow} ${isRegistered ? classes.holidayRowDisabled : ""}`}
                >
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={!isRegistered && selected.has(holiday.date)}
                    disabled={isRegistered || importing}
                    onChange={() => toggle(holiday.date)}
                  />
                  <span className={classes.holidayDate}>{formatLocalDate(holiday.date)}</span>
                  <span className={classes.holidayName}>{t(`names.${holiday.key}`)}</span>
                  {(isRegistered || holiday.optional) && (
                    <span className={classes.holidayTag}>{t(isRegistered ? "registered" : "optional")}</span>
                  )}
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className={classes.dialogActions}>
        <Button variant="outlined" className={classes.ghostButton} onClick={close} disabled={importing}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          className={classes.primaryButton}
          onClick={handleImport}
          disabled={importing || selected.size === 0}
          startIcon={importing ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {t("import", { count: selected.size })}
        </Button>
      </div>
    </Dialog>
  );
};

export default NationalHolidaysDialog;
